import { useEffect, useRef, useState, RefObject } from 'react';

export interface UseRenderedHtmlResult<T extends HTMLElement> {
  ref: RefObject<T>;
  html: string;
}

/**
 * useRenderedHtml
 *
 * Attach `ref` to a DOM element. `html` will contain that element's
 * current innerHTML as a string, updated after each render.
 * @returns {UseRenderedHtmlResult} An object containing the ref to attach to a DOM element and the current HTML string
 */
export function useRenderedHtml<T extends HTMLElement = HTMLElement>(): UseRenderedHtmlResult<T> {
  const ref = useRef<T>(null);
  const [html, setHtml] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const nextHtml = node.innerHTML;

    // Avoid state updates if HTML hasn't changed
    setHtml((prev) => (prev === nextHtml ? prev : nextHtml));
  });

  return { ref, html };
}
